import { Button, IconButton } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';

import CancelIcon from '@mui/icons-material/Cancel';
import ChangeFile from '../../../../shared/components/ChangeFile';
import { ColumnAsset } from '../../../models/LessonComponentContent';
import Constants from '../../../constants';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { GalleryComponent } from '../../../models/LessonComponent';
import { GalleryImage } from './GalleryImage';
import Grid from '@mui/material/Unstable_Grid2';
import { InlineQuillEdit } from '../../InlineQuillEdit';
import NewComponentService from '../../../services/NewComponentService';
import React from 'react';
import { cloneDeep } from 'lodash';
import styles from '../../../styles/Gallery.module.css';
import { useLessonEditContext } from '../../../models/LessonEditContext';

export interface GalleryProps {
  component: GalleryComponent;
}

export const GalleryEditor = (props: GalleryProps) => {
  const items = props.component.content.column || [];
  const settings = props.component.settings?.gallerySettings;

  const { handleSettingsChange } = useLessonEditContext();

  const handleChange = (items: ColumnAsset[]) => {
    const copy = cloneDeep(props.component);
    copy.content.column = items;
    handleSettingsChange(copy);
  };

  const handleAssetSelect = (assetkey: string, currentIndex: number) => {
    const copy = cloneDeep(items);
    copy[currentIndex].assetkey = assetkey;
    handleChange(copy);
  };

  const handleTextChange = (value: string, currentIndex: number) => {
    const copy = cloneDeep(items);
    copy[currentIndex].text = value;
    handleChange(copy);
  };

  const handleAdd = () => {
    const copyItems = items.concat(NewComponentService.createColumns(1));
    handleChange(copyItems);
  };

  const handleDelete = (index: number) => {
    const copyItems = cloneDeep(items);
    copyItems.splice(index, 1);
    handleChange(copyItems);
  };

  const reorderOptions = (fromIndex: number, toIndex: number) => {
    const copyItems = cloneDeep(items);
    const movingItem = items[fromIndex];
    copyItems.splice(fromIndex, 1);
    copyItems.splice(toIndex, 0, movingItem);
    handleChange(copyItems);
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    reorderOptions(result.source.index, result.destination.index);
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  const iconContainerStyles = `${
    settings.shape === 'circle' ? styles.circularImage : styles.squareImage
  }`;

  return (
    <Grid
      container
      direction="column"
      spacing={1}
      alignItems="center"
      sx={{ width: '100%' }}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="images" direction="horizontal">
          {(provided1, snapshot1) => (
            <Grid
              container
              className={styles.container}
              ref={provided1.innerRef}
              style={{
                ...getDroppableStyle(snapshot1.isDraggingOver),
                gap: settings.padding,
                width: '100%'
              }}
            >
              {items?.map((item, index) => (
                <Draggable
                  key={`item${index}`}
                  draggableId={`item${index}`}
                  index={index}
                >
                  {(provided2) => (
                    <Grid
                      container
                      sx={{ width: '100%' }}
                      direction="column"
                      ref={provided2.innerRef}
                      {...provided2.draggableProps}
                    >
                      <Grid
                        container
                        justifyContent="center"
                        sx={{
                          aspectRatio:
                            settings.shape === 'square'
                              ? settings.aspectRatio
                              : '1/1',
                          width: '100%'
                        }}
                        className={styles.imageContainer}
                      >
                        <GalleryImage item={item} shape={settings.shape} />
                        <Grid
                          {...provided2?.dragHandleProps}
                          className={`${iconContainerStyles} ${styles.dragIconContainer}`}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.7)'
                            }
                          }}
                        >
                          <DragIndicatorIcon
                            htmlColor="rgba(0,0,0,0.54)"
                            className={styles.dragIcon}
                          />
                        </Grid>
                        <ChangeFile
                          assetkey={item?.assetkey}
                          assetTypes={['image']}
                          style={{ bottom: '10%' }}
                          handleSelect={(assetkey: string) =>
                            handleAssetSelect(assetkey, index)
                          }
                        />
                        {items?.length > 2 && (
                          <IconButton
                            size="small"
                            className={`${iconContainerStyles} ${styles.cancelIconContainer}`}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.7)'
                              }
                            }}
                            onClick={() => handleDelete(index)}
                          >
                            <CancelIcon />
                          </IconButton>
                        )}
                      </Grid>
                      <Grid
                        className={`center-placeholder ${styles.imageText}`}
                      >
                        <InlineQuillEdit
                          placeholder={'Caption'}
                          value={item.text || NewComponentService.centeredText}
                          componentKey={props.component.componentkey}
                          isCenterAligned
                          styleType={'p'}
                          onChange={(value: string) =>
                            handleTextChange(value, index)
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided1.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>

      <Grid>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleAdd}
          disabled={items?.length >= Constants.GalleryMaxNumImages}
        >
          Add Item
        </Button>
      </Grid>
    </Grid>
  );
};
